import React, { useState, useEffect } from 'react';
import { Popconfirm, Tooltip, message } from 'antd';
import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import { getDownloadUrl, getAttachmentPreviewUrl } from '../../api/projectManagementApi';
import { formatBytes, isImageFile, getOptimisticImageUrl, setOptimisticImageUrl, downloadFileBlob } from '../../utils/attachmentUtils';

/** Pick icon name + styling classes for file type */
function getFileIconInfo(attachment) {
    const name = (attachment?.originalName || attachment?.fileName || '').toLowerCase();
    const mime = (attachment?.mimeType || '').toLowerCase();

    if (mime.includes('pdf') || name.endsWith('.pdf'))
        return { icon: 'picture_as_pdf', bgClass: 'bg-red-50 dark:bg-red-950/80 text-red-600 dark:text-red-400 border border-red-200/80 dark:border-red-900/60', label: 'PDF' };
    if (mime.startsWith('image/') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.gif') || name.endsWith('.svg') || name.endsWith('.webp'))
        return { icon: 'image', bgClass: 'bg-blue-50 dark:bg-blue-950/80 text-blue-600 dark:text-blue-400 border border-blue-200/80 dark:border-blue-900/60', label: 'IMG' };
    if (mime.includes('word') || name.endsWith('.doc') || name.endsWith('.docx') || name.endsWith('.odt'))
        return { icon: 'description', bgClass: 'bg-indigo-50 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400 border border-indigo-200/80 dark:border-indigo-900/60', label: 'DOC' };
    if (mime.includes('sheet') || name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.csv'))
        return { icon: 'table_chart', bgClass: 'bg-emerald-50 dark:bg-emerald-950/80 text-emerald-600 dark:text-emerald-400 border border-emerald-200/80 dark:border-emerald-900/60', label: 'XLS' };
    if (mime.includes('powerpoint') || name.endsWith('.ppt') || name.endsWith('.pptx'))
        return { icon: 'slideshow', bgClass: 'bg-orange-50 dark:bg-orange-950/80 text-orange-600 dark:text-orange-400 border border-orange-200/80 dark:border-orange-900/60', label: 'PPT' };
    if (mime.includes('zip') || mime.includes('rar') || name.endsWith('.zip') || name.endsWith('.rar') || name.endsWith('.7z') || name.endsWith('.tar') || name.endsWith('.gz'))
        return { icon: 'folder_zip', bgClass: 'bg-amber-50 dark:bg-amber-950/80 text-amber-600 dark:text-amber-400 border border-amber-200/80 dark:border-amber-900/60', label: 'ZIP' };
    if (mime.includes('video/') || name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.avi') || name.endsWith('.mkv') || name.endsWith('.webm') || name.endsWith('.3gp'))
        return { icon: 'videocam', bgClass: 'bg-purple-50 dark:bg-purple-950/80 text-purple-600 dark:text-purple-400 border border-purple-200/80 dark:border-purple-900/60', label: 'VID' };
    if (mime.includes('audio/') || name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.m4a') || name.endsWith('.ogg') || name.endsWith('.flac') || name.endsWith('.aac'))
        return { icon: 'audio_file', bgClass: 'bg-pink-50 dark:bg-pink-950/80 text-pink-600 dark:text-pink-400 border border-pink-200/80 dark:border-pink-900/60', label: 'AUD' };
    if (name.endsWith('.txt') || name.endsWith('.md'))
        return { icon: 'article', bgClass: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700', label: 'TXT' };
    return { icon: 'attach_file', bgClass: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700', label: 'FILE' };
}

export default function AttachmentItem({
    attachment,
    taskId,
    onDelete,
    selectable = false,
    selected = false,
    onToggleSelect,
}) {
    const [downloading, setDownloading] = useState(false);
    const [viewing, setViewing] = useState(false);
    const { icon, bgClass } = getFileIconInfo(attachment);

    const isImg = isImageFile(attachment) ||
        (attachment?.mimeType || '').startsWith('image/') ||
        /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(attachment?.originalName || attachment?.fileName || '');

    const initialUrl = attachment?.publicUrl
        || attachment?.previewUrl
        || (isImg && attachment?._id && taskId ? getAttachmentPreviewUrl(taskId, attachment._id) : null)
        || attachment?.downloadUrl
        || (attachment?._id ? getOptimisticImageUrl(attachment._id) : null)
        || (attachment?.originalName ? getOptimisticImageUrl(attachment.originalName) : null)
        || null;

    const [inlineUrl, setInlineUrl] = useState(initialUrl);

    useEffect(() => {
        if (isImg && !inlineUrl) {
            if (attachment?.downloadUrl) {
                setInlineUrl(attachment.downloadUrl);
                return;
            }
            const cached = (attachment?._id ? getOptimisticImageUrl(attachment._id) : null)
                || (attachment?.originalName ? getOptimisticImageUrl(attachment.originalName) : null);
            if (cached) {
                setInlineUrl(cached);
                return;
            }
            if (attachment?._id && taskId) {
                getDownloadUrl(taskId, attachment._id, 'inline')
                    .then(({ data }) => {
                        const url = data?.result?.downloadUrl || data?.result?.publicUrl;
                        if (url) {
                            setInlineUrl(url);
                            setOptimisticImageUrl(attachment._id, url);
                            if (attachment.originalName) setOptimisticImageUrl(attachment.originalName, url);
                        }
                    })
                    .catch(() => { });
            }
        }
    }, [isImg, inlineUrl, attachment?.downloadUrl, attachment?._id, attachment?.originalName, taskId]);

    // Open file in new tab — use publicUrl or inlineUrl synchronously
    const handleView = () => {
        const directUrl = inlineUrl || attachment.publicUrl;
        if (directUrl) {
            window.open(directUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        // Fallback: fetch presigned URL (private bucket).
        setViewing(true);
        getDownloadUrl(taskId, attachment._id, 'inline')
            .then(({ data }) => {
                const targetUrl = data?.result?.downloadUrl || data?.result?.publicUrl;
                if (targetUrl) {
                    window.open(targetUrl, '_blank', 'noopener,noreferrer');
                    setInlineUrl(targetUrl);
                }
            })
            .catch(() => message.error('Could not open attachment.'))
            .finally(() => setViewing(false));
    };

    // Force-download via Blob or presigned attachment stream to guarantee cross-origin files download directly to user's disk
    const handleDownload = async (e) => {
        e?.stopPropagation?.();
        setDownloading(true);
        const fileName = attachment.originalName || attachment.fileName || 'attachment';
        try {
            let downloadTargetUrl = null;
            if (taskId && attachment?._id) {
                try {
                    const { data } = await getDownloadUrl(taskId, attachment._id, 'attachment');
                    downloadTargetUrl = data?.result?.downloadUrl;
                } catch {
                    // fallback to direct url
                }
            }
            if (!downloadTargetUrl) {
                downloadTargetUrl = (attachment?._id && taskId)
                    ? `${getAttachmentPreviewUrl(taskId, attachment._id)}?disposition=attachment`
                    : (inlineUrl || attachment?.downloadUrl || attachment?.publicUrl);
            }

            if (!downloadTargetUrl) {
                message.error('Could not find file download URL.');
                return;
            }

            await downloadFileBlob(downloadTargetUrl, fileName);
        } catch (err) {
            console.error('Attachment download failed:', err);
            const fallbackUrl = (attachment?._id && taskId)
                ? `${getAttachmentPreviewUrl(taskId, attachment._id)}?disposition=attachment`
                : (inlineUrl || attachment?.downloadUrl || attachment?.publicUrl);
            if (fallbackUrl) {
                window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
            } else {
                message.error('Could not download attachment.');
            }
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div
            id={`attachment-${attachment._id}`}
            onClick={handleView}
            className={`flex items-center gap-2.5 p-2.5 bg-white dark:bg-[#1a2538] border rounded-xl w-fit cursor-pointer hover:shadow-sm transition-all group ${
                selected
                    ? 'border-[#03488A] bg-[#f0f7ff] dark:bg-[#152a4a] ring-1 ring-[#03488A]'
                    : 'border-slate-200/80 dark:border-slate-700/80 hover:border-[#03488a]/40 dark:hover:border-blue-500/50'
            }`}
            style={{ maxWidth: 280 }}
        >
            {selectable && (
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleSelect?.(attachment._id);
                    }}
                    className="flex items-center justify-center flex-shrink-0 cursor-pointer -mr-0.5 p-1"
                    title={selected ? 'Deselect attachment' : 'Select attachment'}
                >
                    <input
                        type="checkbox"
                        checked={Boolean(selected)}
                        onChange={() => {}}
                        className="w-4 h-4 rounded text-[#03488A] focus:ring-[#03488A] cursor-pointer accent-[#03488A] pointer-events-none"
                    />
                </div>
            )}

            {/* Thumbnail preview for images, or file-type icon badge for other files */}
            <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden border border-slate-200/60 dark:border-slate-700/60 ${bgClass}`}
            >
                {isImg && (inlineUrl || attachment.publicUrl || attachment.downloadUrl) ? (
                    <img
                        src={inlineUrl || attachment.publicUrl || attachment.downloadUrl}
                        alt={attachment.originalName || 'Thumbnail'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        loading="lazy"
                        onError={() => setInlineUrl(null)}
                    />
                ) : (
                    <span className="material-symbols-outlined text-[20px]">
                        {icon}
                    </span>
                )}
            </div>

            {/* Name + size */}
            <div className="flex-1 min-w-0">
                <div
                    className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate"
                    title={attachment.originalName}
                >
                    {attachment.originalName}
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    {formatBytes(attachment.size)}
                </div>
            </div>

            {/* Actions: Download + Delete */}
            <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-1">
                <Tooltip title="Download">
                    <button
                        onClick={(e) => { e.stopPropagation(); handleDownload(e); }}
                        disabled={downloading || viewing}
                        className="text-slate-400 hover:text-[#03488a] dark:hover:text-blue-400 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                    >
                        {downloading
                            ? <LoadingOutlined style={{ fontSize: 13 }} />
                            : <span className="material-symbols-outlined text-sm">download</span>
                        }
                    </button>
                </Tooltip>
                <Popconfirm
                    title="Remove attachment?"
                    onConfirm={(e) => { e.stopPropagation(); onDelete(attachment._id); }}
                    onCancel={(e) => e.stopPropagation()}
                    okText="Remove"
                    okButtonProps={{ danger: true, size: 'small' }}
                    cancelText="Cancel"
                    placement="topRight"
                >
                    <Tooltip title="Remove">
                        <button
                            onClick={(e) => e.stopPropagation()}
                            className="text-slate-400 hover:text-error p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors cursor-pointer"
                        >
                            <DeleteOutlined style={{ fontSize: 13 }} />
                        </button>
                    </Tooltip>
                </Popconfirm>
            </div>
        </div>
    );
}
